import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Target, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const NUM = (v) => (v === '' || v == null ? '' : Number(v));

const NumField = ({ label, suffix, step, value, testid, onChange }) => (
  <div>
    <Label className="text-xs text-[#78716C]">{label}</Label>
    <div className="relative">
      <Input
        type="number"
        step={step || '1'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="border-[#EDE6D8] rounded-lg h-9"
        data-testid={testid}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#A8A29E]">{suffix}</span>}
    </div>
  </div>
);

export const CxTargetsConfig = ({ bare = false } = {}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');

  const fetchTargets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cx/targets`, { withCredentials: true, headers: authHeaders() });
      setRows(data?.targets || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load CX targets'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  const patch = (eid, field, value) => {
    setRows((rs) => rs.map((r) => {
      if (r.entity_id !== eid) return r;
      const next = { ...r, [field]: value };
      // Keep the monthly ARU target = volume × price/litre in sync as inputs change.
      if (field === 'aru_volume_l' || field === 'price_per_litre') {
        const vol = Number(field === 'aru_volume_l' ? value : r.aru_volume_l) || 0;
        const price = Number(field === 'price_per_litre' ? value : r.price_per_litre) || 0;
        next.aru_target = Math.round(vol * price);
      }
      return next;
    }));
  };

  const patchAddr = (eid, name, value) => {
    setRows((rs) => rs.map((r) => (
      r.entity_id !== eid ? r : { ...r, campus_addressable: { ...(r.campus_addressable || {}), [name]: value } }
    )));
  };

  const save = async (row) => {
    setSavingId(row.entity_id);
    try {
      const addr = {};
      (row.campuses || []).forEach((c) => {
        addr[c.name] = Number((row.campus_addressable || {})[c.name] ?? c.addressable ?? 0) || 0;
      });
      const body = {
        aru_volume_l: NUM(row.aru_volume_l),
        price_per_litre: NUM(row.price_per_litre),
        aru_target: NUM(row.aru_target),
        dormancy_active_max: NUM(row.dormancy_active_max),
        dormancy_cooling_max: NUM(row.dormancy_cooling_max),
        dormancy_at_risk_max: NUM(row.dormancy_at_risk_max),
        habitual_refills: NUM(row.habitual_refills),
        low_balance_threshold: NUM(row.low_balance_threshold),
        target_active_rate: NUM(row.target_active_rate),
        penetration_target: NUM(row.penetration_target),
        sam_percent: NUM(row.sam_percent),
        campus_addressable: addr,
        currency_code: row.currency_code || undefined,
      };
      const { data } = await axios.put(`${API_URL}/api/cx/targets/${row.entity_id}`, body, { withCredentials: true, headers: authHeaders() });
      setRows((rs) => rs.map((r) => (r.entity_id === row.entity_id ? { ...r, ...data, entity_name: r.entity_name, campuses: r.campuses } : r)));
      toast.success(`CX targets saved for ${row.entity_name}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save targets'));
    } finally {
      setSavingId('');
    }
  };

  const nf = (row, field, label, suffix) => (
    <NumField
      label={label}
      suffix={suffix}
      value={row[field]}
      testid={`cxt-${field}-${row.entity_id}`}
      onChange={(v) => patch(row.entity_id, field, v)}
    />
  );

  return (
    <div className={bare ? '' : 'bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6 mt-6'} data-testid="cx-targets-config">
      {!bare && (
        <div className="flex items-center gap-2 mb-1">
          <Target size={20} weight="duotone" className="text-[#0A2540]" />
          <h3 className="text-lg font-semibold text-[#0A2540]">CX Targets</h3>
        </div>
      )}
      <p className="text-sm text-[#78716C] mb-5">
        ARU (Average Revenue per User) targets, dormancy-spectrum thresholds and the habitual-refill bar used across the Customer Experience dashboard. Monthly ARU target auto-calculates as volume × price per litre; you can override it.
      </p>

      {loading ? (
        <p className="text-sm text-[#78716C] py-8 text-center">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#78716C] py-8 text-center">No entities in scope.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => (
            <div key={row.entity_id} className="border border-[#EDE6D8] rounded-xl p-5" data-testid={`cxt-card-${row.entity_id}`}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <p className="font-semibold text-[#0A2540]">{row.entity_name}</p>
                  <p className="text-xs text-[#A8A29E]">
                    {row.currency_code || '—'}{row.is_default ? ' · using defaults' : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => save(row)}
                  disabled={savingId === row.entity_id}
                  className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-9"
                  data-testid={`cxt-save-${row.entity_id}`}
                >
                  <FloppyDisk size={15} className="mr-1.5" /> {savingId === row.entity_id ? 'Saving…' : 'Save'}
                </Button>
              </div>

              <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>ARU Target</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                {nf(row, 'aru_volume_l', 'Volume / month', 'L')}
                {nf(row, 'price_per_litre', `Price / litre (${row.currency_code || ''})`)}
                {nf(row, 'aru_target', `Monthly ARU target (${row.currency_code || ''})`)}
              </div>

              <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Dormancy Spectrum (days since last refill)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {nf(row, 'dormancy_active_max', 'Active ≤', 'd')}
                {nf(row, 'dormancy_cooling_max', 'Cooling ≤', 'd')}
                {nf(row, 'dormancy_at_risk_max', 'At Risk ≤', 'd')}
                {nf(row, 'habitual_refills', 'Habitual refills ≥')}
              </div>

              <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold mb-2 mt-5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Engagement</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {nf(row, 'low_balance_threshold', `Low-balance threshold (${row.currency_code || ''})`)}
              </div>
              <p className="text-[11px] text-[#A8A29E] mt-1.5">Wallet balance at or below this counts as "low" for the Low-balance reminder campaign. Set to 0 to disable that campaign until configured.</p>

              <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold mb-2 mt-5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Active customer rate</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {nf(row, 'target_active_rate', 'Target active rate', '%')}
              </div>
              <p className="text-[11px] text-[#A8A29E] mt-1.5">Active Customer Rate = Active ÷ Registered × 100. Rating bands: ≥75% Excellent · 65–74% Strong · 55–64% Acceptable · 45–54% Warning · &lt;45% Critical.</p>

              <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold mb-2 mt-5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Subscriber penetration</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {nf(row, 'penetration_target', 'Penetration target', '%')}
                {nf(row, 'sam_percent', 'SAM (% of TAM)', '%')}
              </div>
              <p className="text-[11px] text-[#A8A29E] mt-1.5">Penetration Rate = Registered ÷ SAM × 100, where <span className="font-medium text-[#78716C]">SAM = TAM × SAM%</span>. Rating bands: ≥90% Excellent · 80–89% Strong · 70–79% Acceptable · 60–69% Warning · &lt;60% Critical.</p>
              <p className="text-[11px] text-[#78716C] font-medium mt-3 mb-2">Total Addressable Market (TAM) by campus</p>
              {(row.campuses || []).length === 0 ? (
                <p className="text-[11px] text-[#A8A29E]">No campuses found for this entity.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(row.campuses || []).map((c, i) => {
                    const tam = Number((row.campus_addressable || {})[c.name] ?? c.addressable ?? 0) || 0;
                    const samPct = Number(row.sam_percent ?? 100) || 0;
                    const sam = Math.round(tam * samPct / 100);
                    return (
                    <div key={c.name}>
                      <Label className="text-xs text-[#78716C]">{c.name}</Label>
                      <Input
                        type="number"
                        step="1"
                        value={(row.campus_addressable || {})[c.name] ?? c.addressable ?? ''}
                        onChange={(e) => patchAddr(row.entity_id, c.name, e.target.value)}
                        className="border-[#EDE6D8] rounded-lg h-9"
                        data-testid={`cxt-tam-${row.entity_id}-${i}`}
                        placeholder="Total addressable market (TAM)"
                      />
                      <p className="text-[10px] text-[#A8A29E] mt-1">SAM ≈ {sam.toLocaleString()} ({samPct}% of TAM)</p>
                    </div>
                  ); })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
